import {
  loadFromStorage,
  removeFromStorage,
  saveToStorage,
} from "@/utils/storage";

export const THEME_STORAGE_KEY = "THEME";

// Separate from THEME on purpose: THEME is the user's own choice and always
// wins; this only caches the clinic default for the first paint.
export const CLINIC_DEFAULT_STORAGE_KEY = "CLINIC_DEFAULT_THEME";

// Must match ThemeSettingsService::MAX_CLINIC_THEMES.
export const MAX_CLINIC_THEMES = 15;

export const BUILT_IN_THEMES = [
  {
    id: 1,
    themeColor: "#7ce2fe",
    secondaryColor: "#cbcfcd",
    textColor: "#000000",
    bodyColor: "#dfe2df",
    btnColor: "#000000",
    toolsImg: "/images/tools-blue.svg",
    notesImg: "/images/notes-blue.svg",
    profileImg: "/images/profile-blue.svg",
  },
  {
    id: 2,
    themeColor: "#ffc53d",
    secondaryColor: "#ebdaca",
    textColor: "#000000",
    bodyColor: "#f0e4d9",
    btnColor: "#000000",
    toolsImg: "/images/tools-orange.svg",
    notesImg: "/images/notes-orange.svg",
    profileImg: "/images/profile-orange.svg",
  },
  {
    id: 3,
    themeColor: "#bdee63",
    secondaryColor: "rgba(0,0,0,0.2)",
    textColor: "#000000",
    bodyColor: "#dfe2e0",
    btnColor: "#000000",
    toolsImg: "/images/tools-green.svg",
    notesImg: "/images/notes-green.svg",
    profileImg: "/images/profile-green.svg",
  },
  {
    id: 4,
    themeColor: "yellow",
    secondaryColor: "#f0f0d9",
    textColor: "#000000",
    bodyColor: "#e6e6e6",
    btnColor: "#000000",
    toolsImg: "/images/tools-black.svg",
    notesImg: "/images/notes-black.svg",
    profileImg: "/images/profile-black.svg",
  },
  {
    id: 5,
    themeColor: "#f014b2",
    secondaryColor: "#ffabde",
    textColor: "#333333",
    bodyColor: "#e8c4da",
    btnColor: "#ffffff",
    toolsImg: "/images/tools-black.svg",
    notesImg: "/images/notes-black.svg",
    profileImg: "/images/profile-black.svg",
  },
];

export const BUILT_IN_THEME_COUNT = BUILT_IN_THEMES.length;
export const MAX_TOTAL_THEMES = BUILT_IN_THEME_COUNT + MAX_CLINIC_THEMES;

export const ACCENT_ONLY_BASE = {
  secondaryColor: "#cbcfcd",
  textColor: "#000000",
  bodyColor: "#dfe2df",
  toolsImg: "/images/tools-black.svg",
  notesImg: "/images/notes-black.svg",
  profileImg: "/images/profile-black.svg",
};

export function toRgb(color) {
  if (!color) return { red: 0, green: 0, blue: 0, alpha: 1 };
  // Built-in 4 is the CSS keyword "yellow", so let the browser resolve non-hex values.
  if (color.charAt(0) !== "#") {
    const probe = document.createElement("div");
    probe.style.color = color;
    document.body.appendChild(probe);
    const parts = (getComputedStyle(probe).color.match(/\d+/g) || []).map(
      Number
    );
    document.body.removeChild(probe);
    return {
      red: parts[0] || 0,
      green: parts[1] || 0,
      blue: parts[2] || 0,
      alpha: 1,
    };
  }
  let hex = color.slice(1);
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((c) => c + c)
      .join("");
  }
  const n = parseInt(hex, 16);
  return {
    red: (n >> 16) & 255,
    green: (n >> 8) & 255,
    blue: n & 255,
    alpha: 1,
  };
}

export function rgbToOpaqueHex(color) {
  const to = (n) =>
    Math.round(Math.min(255, Math.max(0, n)))
      .toString(16)
      .padStart(2, "0");
  return `#${to(color.red)}${to(color.green)}${to(color.blue)}`;
}

export function readableTextColorOn(color) {
  const { red, green, blue } = toRgb(color);
  const channel = (v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const luminance =
    0.2126 * channel(red) + 0.7152 * channel(green) + 0.0722 * channel(blue);
  // Compares the two WCAG contrast ratios; they cross at 0.1791, not at 0.5.
  const onBlack = (luminance + 0.05) / 0.05;
  const onWhite = 1.05 / (luminance + 0.05);
  return onBlack >= onWhite ? "#000000" : "#ffffff";
}

export function unsetCustomTheme(defaultAccent) {
  return {
    id: "custom",
    themeColor: defaultAccent,
    btnColor: readableTextColorOn(defaultAccent),
    ...ACCENT_ONLY_BASE,
  };
}

// Must match ThemeSettingsService::ID_PREFIX.
export function isClinicThemeId(id) {
  return typeof id === "string" && id.startsWith("clinic-");
}

export function hydrateClinicTheme(clinicTheme) {
  return {
    id: clinicTheme.id,
    name: clinicTheme.name,
    themeColor: clinicTheme.themeColor,
    btnColor: readableTextColorOn(clinicTheme.themeColor),
    ...ACCENT_ONLY_BASE,
  };
}

export function clinicDefaultTheme(themeSettings) {
  const themes = themeSettings?.themes || [];
  const defaultThemeId = themeSettings?.default_theme_id;
  if (!defaultThemeId) return null;
  const match = themes.find((theme) => theme.id === defaultThemeId);
  return match ? hydrateClinicTheme(match) : null;
}

export function activeTheme(themeSettings = null) {
  return (
    readStoredTheme() ||
    clinicDefaultTheme(themeSettings) ||
    readCachedClinicDefault() ||
    BUILT_IN_THEMES[0]
  );
}

function readStoredThemeAt(key) {
  const stored = loadFromStorage(key);
  const isPlainObject =
    stored && typeof stored === "object" && !Array.isArray(stored);
  return isPlainObject ? stored : null;
}

export function readStoredTheme() {
  return readStoredThemeAt(THEME_STORAGE_KEY);
}

export function readCachedClinicDefault() {
  return readStoredThemeAt(CLINIC_DEFAULT_STORAGE_KEY);
}

export const CLINIC_THEME_SYNC = {
  NOT_APPLICABLE: "not-applicable",
  UNCHANGED: "unchanged",
  UPDATED: "updated",
  // Storage is left alone on purpose: the user keeps their colours until they pick another.
  GONE: "gone",
};

export function syncStoredClinicTheme(themeSettings) {
  const stored = readStoredTheme();

  if (!stored || !isClinicThemeId(stored.id)) {
    return { status: CLINIC_THEME_SYNC.NOT_APPLICABLE, theme: stored };
  }

  const match = (themeSettings?.themes || []).find(
    (theme) => theme.id === stored.id
  );

  if (!match) {
    return { status: CLINIC_THEME_SYNC.GONE, theme: stored };
  }

  if (match.themeColor === stored.themeColor && match.name === stored.name) {
    return { status: CLINIC_THEME_SYNC.UNCHANGED, theme: stored };
  }

  const fresh = hydrateClinicTheme(match);
  saveToStorage(THEME_STORAGE_KEY, fresh);

  return { status: CLINIC_THEME_SYNC.UPDATED, theme: fresh };
}

export function cacheClinicDefaultTheme(theme) {
  if (theme) {
    saveToStorage(CLINIC_DEFAULT_STORAGE_KEY, theme);
    return;
  }
  removeFromStorage(CLINIC_DEFAULT_STORAGE_KEY);
}

export function dispatchThemeChanged(themeColor) {
  window.dispatchEvent(
    new CustomEvent("theme-changed", { detail: { themeColor } })
  );
}

export function applyTheme(theme) {
  const root = document.documentElement.style;
  root.setProperty("--theme-color", theme.themeColor);
  root.setProperty("--secondary-color", theme.secondaryColor);
  root.setProperty("--body-bg-color", theme.bodyColor);
  root.setProperty("--main-text-color", theme.textColor);
  root.setProperty("--btn-text-color", theme.btnColor);
  root.setProperty("--tools-img-path", `url('${theme.toolsImg}')`);
  root.setProperty("--notes-img-path", `url('${theme.notesImg}')`);
  root.setProperty("--profile-img-path", `url('${theme.profileImg}')`);
}
